import { useCallback, useEffect, useState } from "react";

// Favorites, persisted per-browser in localStorage (the app is anonymous — no
// accounts to sync across devices). Generic over the stored item shape so we can
// favorite countries and elections; we store the full display object (not just
// the id) so the Favorites section renders without extra fetches.

export function useFavorites<T extends { id: string }>(namespace: string) {
  const key = `warurna:fav:${namespace}`;

  const [items, setItems] = useState<T[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(key) ?? "[]") as T[];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(items));
    } catch {
      // storage unavailable (private mode) — favorites just won't persist
    }
  }, [key, items]);

  const isFav = useCallback((id: string) => items.some((i) => i.id === id), [items]);

  const toggle = useCallback((item: T) => {
    setItems((prev) =>
      prev.some((i) => i.id === item.id) ? prev.filter((i) => i.id !== item.id) : [...prev, item],
    );
  }, []);

  return { items, isFav, toggle };
}
